import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertTriangle, Copy, CopyPlus, ExternalLink, Plus, Trash2, Wand2 } from "lucide-react";
import { rhm, apiError, money, titleCase } from "../api";
import { useRhmAuth } from "../RhmAuthContext";
import { Badge, Btn, Card, Empty, Field, Input, Modal, PageHead, Select, Table, Td, Textarea, statusTone } from "../ui";
import { RowEditor } from "../editors";
import { LiveSwitch } from "../components/LiveSwitch";
import { CoverImageField } from "../components/EbookAssets";

const EMPTY = {
    name: "", product_code: "", sku: "", division: "Nutraceuticals", product_type: "",
    status: "draft", pack_size: "", unit_label: "unit", mrp: "", selling_price: "", purchase_price: "",
    gst: 18, min_stock: 0, reorder_qty: 0, consumption_period_days: 30, manufacturer: "",
    short_description: "", description: "", flavor: "", images: "", initial_quantity: 0,
    composition: [], shop_type: "product", digital_free: false, sessions_label: "", consult_area: "",
};

const KINDS = [
    { value: "product", title: "Product", desc: "A physical supplement with stock, batches and formulation." },
    { value: "ebook", title: "Ebook / Guide", desc: "A downloadable PDF guide — free, or paid and unlocked after purchase." },
    { value: "consultation_pack", title: "Consultation Pack", desc: "A paid pack of consultation sessions customers can book." },
];

const KIND_DEFAULTS = {
    product: {},
    ebook: { division: "Nutraceuticals", product_type: "Digital Guide", unit_label: "download", gst: 0 },
    consultation_pack: { division: "Nutraceuticals", product_type: "Consultation Pack", unit_label: "pack", gst: 18 },
};

const INLINE_CELL = "bg-transparent border border-transparent hover:border-white/15 focus:border-gold " +
    "focus:outline-none px-2 py-1 text-sm transition-colors cursor-pointer [&>option]:bg-bg1 [&>option]:text-zinc-200";

const STATUS_TEXT = {
    active: "text-lime", coming_soon: "text-gold", draft: "text-zinc-400",
    out_of_stock: "text-red-400", discontinued: "text-red-400",
    temporarily_unavailable: "text-orange-300", pending_approval: "text-gold",
};

/** Click-to-change cell in the products list — nothing is saved until SAVE ALL. */
const InlineCell = ({ testId, dirty, children }) => (
    <div className={`inline-flex items-center ${dirty ? "ring-1 ring-gold/60 bg-gold/10" : ""}`} data-testid={testId}>
        {children}
    </div>
);

export default function RhmProducts() {
    const { can } = useRhmAuth();
    const nav = useNavigate();
    const qc = useQueryClient();
    const [open, setOpen] = useState(false);
    const [kind, setKind] = useState("");
    const [form, setForm] = useState(EMPTY);
    const [q, setQ] = useState("");
    const [doomed, setDoomed] = useState(null);

    const startNew = () => { setKind(""); setForm(EMPTY); setOpen(true); };
    const pickKind = (value) => {
        setKind(value);
        setForm({ ...EMPTY, ...KIND_DEFAULTS[value], shop_type: value });
    };

    const { data: products = [], isLoading } = useQuery({
        queryKey: ["rhm-products"],
        queryFn: async () => (await rhm.get("/products")).data,
    });
    const { data: meta } = useQuery({
        queryKey: ["rhm-product-meta"],
        queryFn: async () => (await rhm.get("/product-meta")).data,
    });

    const duplicate = useMutation({
        mutationFn: async (id) => (await rhm.post(`/products/${id}/duplicate`)).data,
        onSuccess: (p) => {
            qc.invalidateQueries({ queryKey: ["rhm-products"] });
            toast.success(`${p.name} created as ${p.product_code} — edit the copy`);
            nav(`/rhm/products/${p.id}`);
        },
        onError: (e) => toast.error(apiError(e)),
    });

    const discontinue = useMutation({
        mutationFn: async (id) => (await rhm.delete(`/products/${id}`)).data,
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["rhm-products"] });
            toast.success(`${doomed.name} discontinued — hidden from the website`);
            setDoomed(null);
        },
        onError: (e) => toast.error(apiError(e)),
    });

    const destroy = useMutation({
        mutationFn: async (id) => (await rhm.delete(`/products/${id}/permanent`)).data,
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["rhm-products"] });
            toast.success(`${doomed.name} deleted`);
            setDoomed(null);
        },
        onError: (e) => toast.error(apiError(e)),
    });

    const create = useMutation({
        mutationFn: async (payload) => (await rhm.post("/products", payload)).data,
        onSuccess: (p) => {
            toast.success(`${p.name} created`);
            setOpen(false);
            setKind("");
            setForm(EMPTY);
            qc.invalidateQueries({ queryKey: ["rhm-products"] });
        },
        onError: (e) => toast.error(apiError(e)),
    });

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = (e) => {
        e.preventDefault();
        const num = (v) => (v === "" || v === null ? null : Number(v));
        create.mutate({
            ...form,
            shop_type: kind || "product",
            mrp: num(form.mrp) ?? 0,
            selling_price: num(form.selling_price),
            purchase_price: num(form.purchase_price),
            gst: num(form.gst) ?? 18,
            min_stock: num(form.min_stock) ?? 0,
            reorder_qty: num(form.reorder_qty) ?? 0,
            consumption_period_days: num(form.consumption_period_days) ?? 30,
            initial_quantity: num(form.initial_quantity) ?? 0,
            images: form.images ? form.images.split("\n").map((s) => s.trim()).filter(Boolean) : [],
            composition: (form.composition || []).filter((c) => c.ingredient),
        });
    };

    const rows = products.filter((p) =>
        !q || [p.name, p.product_code, p.sku].join(" ").toLowerCase().includes(q.toLowerCase()));

    const canEdit = can("products.edit");
    const [edits, setEdits] = useState({});
    const dirtyRows = Object.keys(edits);

    const original = (p, key) => (key === "stock_available"
        ? (p.stock?.available ?? 0)
        : (p[key] ?? ""));

    const applyEdit = (p, key, value) => setEdits((prev) => {
        const row = { ...(prev[p.id] || {}) };
        if (value === "" || String(value) === String(original(p, key))) delete row[key];
        else row[key] = value;
        const next = { ...prev };
        if (Object.keys(row).length) next[p.id] = row;
        else delete next[p.id];
        return next;
    });

    const valueOf = (p, key) => {
        const row = edits[p.id] || {};
        return key in row ? row[key] : original(p, key);
    };
    const isDirty = (p, key) => key in (edits[p.id] || {});

    const saveAll = useMutation({
        mutationFn: async () => {
            const entries = Object.entries(edits);
            for (const [id, patch] of entries) {
                const payload = {};
                if ("status" in patch) payload.status = patch.status;
                if ("division" in patch) payload.division = patch.division;
                if ("mrp" in patch) payload.mrp = Number(patch.mrp);
                if ("selling_price" in patch) payload.selling_price = Number(patch.selling_price);
                if ("min_stock" in patch) payload.min_stock = parseInt(patch.min_stock, 10);
                if ("stock_available" in patch) payload.stock_available = parseInt(patch.stock_available, 10);
                await rhm.patch(`/products/${id}/quick`, payload);
            }
            return entries.length;
        },
        onSuccess: (n) => {
            setEdits({});
            qc.invalidateQueries({ queryKey: ["rhm-products"] });
            toast.success(`${n} product${n === 1 ? "" : "s"} updated — live on the website`);
        },
        onError: (e) => {
            qc.invalidateQueries({ queryKey: ["rhm-products"] });
            toast.error(apiError(e));
        },
    });

    const setLive = useMutation({
        mutationFn: async ({ id, live }) => (await rhm.patch(`/products/${id}/quick`, { page_live: live })).data,
        onSuccess: (_d, v) => {
            qc.invalidateQueries({ queryKey: ["rhm-products"] });
            toast.success(v.live ? "Product page is live again" : "Product page switched off");
        },
        onError: (e) => toast.error(apiError(e)),
    });

    return (
        <div data-testid="rhm-products-page">
            <PageHead
                title="Products"
                sub="Product Master — click any division, status, price or stock cell to change it, then SAVE ALL."
                action={can("products.create") && (
                    <div className="flex items-center gap-2">
                        <Btn variant="ghost" onClick={() => nav("/rhm/products/quick")} data-testid="rhm-quick-add-button">
                            <Wand2 className="w-3.5 h-3.5 inline mr-1.5" /> QUICK ADD
                        </Btn>
                        <Btn onClick={startNew} data-testid="rhm-add-product-button">
                            <Plus className="w-3.5 h-3.5 inline mr-1.5" /> ADD PRODUCT
                        </Btn>
                    </div>
                )}
            />

            <Card
                title={`${rows.length} product(s)`}
                action={<Input placeholder="Search name / code / SKU" value={q} onChange={(e) => setQ(e.target.value)}
                    className="!w-56" data-testid="rhm-product-search" />}
            >
                {dirtyRows.length > 0 && (
                    <div className="sticky top-0 z-20 mb-3 flex items-center justify-between gap-3 border border-gold/40 bg-gold/10 px-4 py-2.5"
                        data-testid="rhm-inline-save-bar">
                        <span className="mono text-[10px] tracking-[0.16em] text-gold">
                            {dirtyRows.length} PRODUCT{dirtyRows.length === 1 ? "" : "S"} CHANGED — NOT SAVED YET
                        </span>
                        <div className="flex items-center gap-2">
                            <Btn variant="ghost" onClick={() => setEdits({})} data-testid="rhm-inline-discard">DISCARD</Btn>
                            <Btn onClick={() => saveAll.mutate()} disabled={saveAll.isPending} data-testid="rhm-inline-save">
                                {saveAll.isPending ? "SAVING…" : "SAVE ALL"}
                            </Btn>
                        </div>
                    </div>
                )}
                {isLoading ? <div className="mono text-xs text-zinc-500">LOADING…</div> : (
                    <Table testId="rhm-products-table"
                        head={["Code", "Product", "Live", "Division", "Status", "MRP", "Selling", "Stock", "Min", ""]}
                        empty={!rows.length && <Empty msg="No products match." />}>
                        {rows.map((p) => (
                            <tr key={p.id} data-testid={`rhm-product-row-${p.product_code}`}>
                                <Td className="mono text-xs text-gold">{p.product_code}</Td>
                                <Td>
                                    <div className="font-semibold">{p.name}</div>
                                    <div className="text-xs text-zinc-500">
                                        {p.shop_type === "ebook" ? "Ebook / Guide"
                                            : p.shop_type === "consultation_pack" ? "Consultation Pack"
                                                : (p.pack_size || p.product_type)}
                                    </div>
                                    <div className="mt-1.5 flex items-center gap-3">
                                        <a href={`/product/${p.slug}`} target="_blank" rel="noreferrer"
                                            data-testid={`rhm-view-live-${p.product_code}`}
                                            className="mono text-[9px] tracking-[0.16em] text-lime hover:text-gold transition-colors inline-flex items-center gap-1">
                                            <ExternalLink className="w-3 h-3" /> /product/{p.slug}
                                        </a>
                                        <button type="button" data-testid={`rhm-copy-link-${p.product_code}`}
                                            title="Copy the public link"
                                            onClick={() => {
                                                navigator.clipboard?.writeText(`${window.location.origin}/product/${p.slug}`);
                                                toast.success("Link copied");
                                            }}
                                            className="text-zinc-600 hover:text-gold transition-colors">
                                            <Copy className="w-3 h-3" />
                                        </button>
                                    </div>
                                </Td>
                                <Td>
                                    <LiveSwitch live={p.page_live !== false} disabled={!canEdit || setLive.isPending}
                                        testId={`rhm-live-${p.product_code}`} previewHref={`/product/${p.slug}`}
                                        onChange={(live) => setLive.mutate({ id: p.id, live })} />
                                </Td>
                                <Td className="text-sm text-zinc-400">
                                    {canEdit ? (
                                        <InlineCell testId={`rhm-cell-division-${p.product_code}`} dirty={isDirty(p, "division")}>
                                            <select value={valueOf(p, "division")} className={`${INLINE_CELL} text-zinc-300`}
                                                data-testid={`rhm-inline-division-${p.product_code}`}
                                                onChange={(e) => applyEdit(p, "division", e.target.value)}>
                                                {(meta?.divisions || [p.division]).map((d) => (
                                                    <option key={d} value={d}>{d}</option>
                                                ))}
                                            </select>
                                        </InlineCell>
                                    ) : p.division}
                                </Td>
                                <Td>
                                    {canEdit ? (
                                        <InlineCell testId={`rhm-cell-status-${p.product_code}`} dirty={isDirty(p, "status")}>
                                            <select value={valueOf(p, "status")}
                                                className={`${INLINE_CELL} mono text-[10px] tracking-[0.14em] uppercase ${
                                                    STATUS_TEXT[valueOf(p, "status")] || "text-zinc-300"}`}
                                                data-testid={`rhm-inline-status-${p.product_code}`}
                                                onChange={(e) => applyEdit(p, "status", e.target.value)}>
                                                {(meta?.statuses || [p.status]).map((s) => (
                                                    <option key={s} value={s}>{titleCase(s)}</option>
                                                ))}
                                            </select>
                                        </InlineCell>
                                    ) : <Badge tone={statusTone(p.status)}>{titleCase(p.status || "")}</Badge>}
                                </Td>
                                <Td>
                                    {canEdit ? (
                                        <InlineCell testId={`rhm-cell-mrp-${p.product_code}`} dirty={isDirty(p, "mrp")}>
                                            <span className="text-zinc-600 text-xs pl-1">₹</span>
                                            <input type="number" min="0" step="0.01" value={valueOf(p, "mrp")}
                                                className={`${INLINE_CELL} w-24 text-right`}
                                                data-testid={`rhm-inline-mrp-${p.product_code}`}
                                                onChange={(e) => applyEdit(p, "mrp", e.target.value)} />
                                        </InlineCell>
                                    ) : money(p.mrp)}
                                </Td>
                                <Td>
                                    {canEdit ? (
                                        <InlineCell testId={`rhm-cell-selling-${p.product_code}`} dirty={isDirty(p, "selling_price")}>
                                            <span className="text-zinc-600 text-xs pl-1">₹</span>
                                            <input type="number" min="0" step="0.01" value={valueOf(p, "selling_price")}
                                                className={`${INLINE_CELL} w-24 text-right`} placeholder="—"
                                                data-testid={`rhm-inline-selling-${p.product_code}`}
                                                onChange={(e) => applyEdit(p, "selling_price", e.target.value)} />
                                        </InlineCell>
                                    ) : money(p.selling_price)}
                                </Td>
                                <Td>
                                    {canEdit && can("inventory.edit") && p.shop_type === "product" ? (
                                        <InlineCell testId={`rhm-cell-stock-${p.product_code}`} dirty={isDirty(p, "stock_available")}>
                                            <input type="number" min="0" value={valueOf(p, "stock_available")}
                                                className={`${INLINE_CELL} w-20 text-right ${p.low_stock ? "text-red-400 font-bold" : ""}`}
                                                data-testid={`rhm-inline-stock-${p.product_code}`}
                                                onChange={(e) => applyEdit(p, "stock_available", e.target.value)} />
                                            <span className="text-zinc-600 text-xs pr-1">{p.unit_label}s</span>
                                        </InlineCell>
                                    ) : (
                                        <>
                                            <span className={p.low_stock ? "text-red-400 font-bold" : ""}>
                                                {p.stock?.available ?? 0}
                                            </span>
                                            <span className="text-zinc-600 text-xs"> {p.unit_label}s</span>
                                        </>
                                    )}
                                </Td>
                                <Td className="text-sm text-zinc-500">
                                    {canEdit ? (
                                        <InlineCell testId={`rhm-cell-min-${p.product_code}`} dirty={isDirty(p, "min_stock")}>
                                            <input type="number" min="0" value={valueOf(p, "min_stock")}
                                                className={`${INLINE_CELL} w-16 text-right`}
                                                data-testid={`rhm-inline-min-${p.product_code}`}
                                                onChange={(e) => applyEdit(p, "min_stock", e.target.value)} />
                                        </InlineCell>
                                    ) : p.min_stock}
                                </Td>
                                <Td>
                                    <div className="flex items-center gap-3">
                                        <Link to={`/rhm/products/${p.id}`} data-testid={`rhm-open-product-${p.product_code}`}
                                            className="mono text-[10px] tracking-[0.16em] text-gold">OPEN</Link>
                                        {can("products.create") && (
                                            <button type="button" onClick={() => duplicate.mutate(p.id)}
                                                title="Duplicate this product" disabled={duplicate.isPending}
                                                data-testid={`rhm-duplicate-product-${p.product_code}`}
                                                className="text-zinc-500 hover:text-lime transition-colors">
                                                <CopyPlus className="w-3.5 h-3.5" />
                                            </button>
                                        )}
                                        {can("products.delete") && (
                                            <button type="button" onClick={() => setDoomed(p)} title="Delete product"
                                                data-testid={`rhm-delete-product-${p.product_code}`}
                                                className="text-zinc-500 hover:text-red-400 transition-colors">
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        )}
                                    </div>
                                </Td>
                            </tr>
                        ))}
                    </Table>
                )}
            </Card>

            <Modal open={!!doomed} onClose={() => setDoomed(null)} testId="rhm-delete-product-modal"
                title={doomed ? `Delete ${doomed.name}?` : ""}>
                {doomed && (
                    <div>
                        <div className="flex items-start gap-3 border border-red-500/30 bg-red-500/[0.06] p-4">
                            <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                            <div className="text-sm text-zinc-300">
                                <strong className="text-white">{doomed.product_code} · {doomed.name}</strong>
                                <p className="mt-2 text-zinc-400">
                                    Deleting removes the product, its batches, stock history, notify-me requests
                                    and its Science record for good. Its ingredients stay in the Science
                                    Ingredient Library. A product that appears in an order cannot be deleted —
                                    discontinue it instead.
                                </p>
                            </div>
                        </div>
                        <div className="mt-5 flex flex-wrap items-center gap-3">
                            <Btn variant="danger" onClick={() => destroy.mutate(doomed.id)}
                                disabled={destroy.isPending} data-testid="rhm-confirm-delete-product">
                                {destroy.isPending ? "DELETING…" : "DELETE FOREVER"}
                            </Btn>
                            <Btn variant="ghost" onClick={() => discontinue.mutate(doomed.id)}
                                disabled={discontinue.isPending} data-testid="rhm-discontinue-product">
                                {discontinue.isPending ? "SAVING…" : "JUST DISCONTINUE"}
                            </Btn>
                            <Btn variant="ghost" onClick={() => setDoomed(null)} data-testid="rhm-cancel-delete-product">
                                CANCEL
                            </Btn>
                        </div>
                    </div>
                )}
            </Modal>

            <Modal open={open} onClose={() => setOpen(false)} wide testId="rhm-add-product-modal"
                title={kind ? `Add ${KINDS.find((k) => k.value === kind).title}` : "What are you adding?"}>
                {!kind ? (
                    <div className="grid md:grid-cols-3 gap-4" data-testid="pf-kind-chooser">
                        {KINDS.map((k) => (
                            <button key={k.value} type="button" onClick={() => pickKind(k.value)}
                                data-testid={`pf-kind-${k.value}`}
                                className="text-left border border-white/10 p-5 hover:border-gold hover:bg-white/[0.03] transition-colors">
                                <div className="mono text-[10px] tracking-[0.2em] text-gold">{k.title.toUpperCase()}</div>
                                <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{k.desc}</p>
                            </button>
                        ))}
                    </div>
                ) : (
                <form onSubmit={submit} className="space-y-6">
                    <button type="button" onClick={() => setKind("")} data-testid="pf-kind-change"
                        className="mono text-[10px] tracking-[0.16em] text-zinc-500 hover:text-gold">
                        ← CHANGE TYPE
                    </button>

                    <div>
                        <div className="mono text-[10px] tracking-[0.2em] text-gold mb-3">BASIC INFORMATION</div>
                        <div className="grid md:grid-cols-2 gap-4">
                            <Field label={kind === "product" ? "Product name" : "Title"} required>
                                <Input required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="pf-name" />
                            </Field>
                            <Field label="Item code" required hint="Unique, e.g. RHM02 / EB01 / CP01">
                                <Input required value={form.product_code} onChange={(e) => set("product_code", e.target.value.toUpperCase())} data-testid="pf-code" />
                            </Field>
                            <Field label="SKU" hint="Defaults to the item code">
                                <Input value={form.sku} onChange={(e) => set("sku", e.target.value.toUpperCase())} data-testid="pf-sku" />
                            </Field>
                            <Field label="Status" hint="Set to Active to show it on the website">
                                <Select value={form.status} onChange={(e) => set("status", e.target.value)}
                                    options={meta?.statuses || ["draft"]} data-testid="pf-status" />
                            </Field>
                            {kind === "product" && (
                                <>
                                    <Field label="Division">
                                        <Select value={form.division} onChange={(e) => set("division", e.target.value)}
                                            options={meta?.divisions || ["Nutraceuticals"]} data-testid="pf-division" />
                                    </Field>
                                    <Field label="Product type">
                                        <Input value={form.product_type} onChange={(e) => set("product_type", e.target.value)}
                                            placeholder="Effervescent Tablet" data-testid="pf-type" />
                                    </Field>
                                    <Field label="Pack size" hint="e.g. 15 tablets per tube">
                                        <Input value={form.pack_size} onChange={(e) => set("pack_size", e.target.value)} data-testid="pf-pack" />
                                    </Field>
                                    <Field label="Stock unit label" hint="tube, bottle, box…">
                                        <Input value={form.unit_label} onChange={(e) => set("unit_label", e.target.value)} data-testid="pf-unit" />
                                    </Field>
                                </>
                            )}
                            {kind === "consultation_pack" && (
                                <>
                                    <Field label="Sessions label" hint="Shown on the card, e.g. 3 sessions · 30 min each">
                                        <Input value={form.sessions_label} data-testid="pf-sessions"
                                            onChange={(e) => set("sessions_label", e.target.value)} />
                                    </Field>
                                    <Field label="Consultation area key" hint="Used by the Book an appointment button, e.g. hair">
                                        <Input value={form.consult_area} data-testid="pf-consult-area"
                                            onChange={(e) => set("consult_area", e.target.value)} />
                                    </Field>
                                </>
                            )}
                            {kind === "ebook" && (
                                <>
                                    <Field label="Pricing">
                                        <label className="flex items-center gap-2 text-sm mt-2">
                                            <input type="checkbox" checked={form.digital_free} data-testid="pf-digital-free"
                                                onChange={(e) => set("digital_free", e.target.checked)} />
                                            Free download (no purchase needed)
                                        </label>
                                    </Field>
                                    <Field label="Format" hint="Pages, language — shown as a subtitle">
                                        <Input value={form.pack_size} data-testid="pf-format"
                                            onChange={(e) => set("pack_size", e.target.value)}
                                            placeholder="24 pages · English · PDF" />
                                    </Field>
                                </>
                            )}
                            <Field label={kind === "product" ? "Short description" : "Tagline"} className="md:col-span-2">
                                <Input value={form.short_description} data-testid="pf-short"
                                    onChange={(e) => set("short_description", e.target.value)} />
                            </Field>
                            <Field label="Full description" className="md:col-span-2">
                                <Textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="pf-description" />
                            </Field>
                            <div className="md:col-span-2">
                                <CoverImageField urls={form.images ? form.images.split("\n").map((s) => s.trim()).filter(Boolean) : []}
                                    testId="pf-cover" label={kind === "ebook" ? "Cover image" : "Main image"}
                                    onChange={(urls) => set("images", urls.join("\n"))} />
                            </div>
                            <Field label={kind === "ebook" ? "Cover image URLs" : "Image URLs"} hint="One per line — uploads land here too" className="md:col-span-2">
                                <Textarea rows={2} value={form.images} onChange={(e) => set("images", e.target.value)} data-testid="pf-images" />
                            </Field>
                        </div>
                    </div>

                    <div>
                        <div className="mono text-[10px] tracking-[0.2em] text-gold mb-3">PRICING</div>
                        <div className="grid md:grid-cols-3 gap-4">
                            <Field label={form.digital_free ? "MRP (₹) — keep 0 for free" : "MRP (₹)"} required>
                                <Input type="number" min="0" step="0.01" required value={form.mrp}
                                    onChange={(e) => set("mrp", e.target.value)} data-testid="pf-mrp" />
                            </Field>
                            <Field label="Selling price (₹)" hint="Blank = same as MRP">
                                <Input type="number" min="0" step="0.01" value={form.selling_price}
                                    onChange={(e) => set("selling_price", e.target.value)} data-testid="pf-selling" />
                            </Field>
                            <Field label="GST %">
                                <Input type="number" min="0" value={form.gst} onChange={(e) => set("gst", e.target.value)} data-testid="pf-gst" />
                            </Field>
                            {kind === "product" && (
                                <>
                                    <Field label="Purchase price (₹)" hint="May stay blank">
                                        <Input type="number" min="0" step="0.01" value={form.purchase_price}
                                            onChange={(e) => set("purchase_price", e.target.value)} data-testid="pf-purchase" />
                                    </Field>
                                    <Field label="Minimum stock level">
                                        <Input type="number" min="0" value={form.min_stock}
                                            onChange={(e) => set("min_stock", e.target.value)} data-testid="pf-min-stock" />
                                    </Field>
                                    <Field label="Reorder quantity">
                                        <Input type="number" min="0" value={form.reorder_qty}
                                            onChange={(e) => set("reorder_qty", e.target.value)} data-testid="pf-reorder" />
                                    </Field>
                                    <Field label="Manufacturer">
                                        <Input value={form.manufacturer} onChange={(e) => set("manufacturer", e.target.value)} data-testid="pf-manufacturer" />
                                    </Field>
                                    <Field label="Expected consumption period (days)" hint="Drives reorder prediction">
                                        <Input type="number" min="1" value={form.consumption_period_days}
                                            onChange={(e) => set("consumption_period_days", e.target.value)} />
                                    </Field>
                                    <Field label="Initial quantity" hint="Creates an opening-stock batch">
                                        <Input type="number" min="0" value={form.initial_quantity}
                                            onChange={(e) => set("initial_quantity", e.target.value)} data-testid="pf-initial-qty" />
                                    </Field>
                                </>
                            )}
                        </div>
                    </div>

                    {kind === "product" && (
                        <div>
                            <div className="mono text-[10px] tracking-[0.2em] text-gold mb-3">COMPOSITION</div>
                            <RowEditor
                                testId="pf-composition"
                                value={form.composition}
                                onChange={(v) => set("composition", v)}
                                fields={[
                                    { key: "ingredient", label: "Ingredient" },
                                    { key: "strength", label: "Strength / quantity", placeholder: "600" },
                                    { key: "unit", label: "Unit", placeholder: "mg" },
                                    { key: "note", label: "Note", wide: true },
                                ]}
                            />
                        </div>
                    )}

                    {kind === "ebook" && (
                        <p className="text-sm text-zinc-500 border border-white/10 p-4">
                            Save this first, then upload the PDF on the item's <strong className="text-gold">SHOP</strong> tab.
                            Paid guides are stamped with the buyer's name and stay on our own server — no outside links.
                        </p>
                    )}

                    <div className="flex gap-3 pt-2">
                        <Btn type="submit" disabled={create.isPending} data-testid="pf-submit">
                            {create.isPending ? "SAVING…" : "CREATE"}
                        </Btn>
                        <Btn type="button" variant="ghost" onClick={() => setOpen(false)}>CANCEL</Btn>
                    </div>
                </form>
                )}
            </Modal>
        </div>
    );
}
